import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authApi from './authApi';
import { clearTokens, getAccessToken, setOnUnauthorized, setTokens, getRefreshToken } from '../api/client';

const SESSION_CHECK_INTERVAL_MS = 30_000;

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {string} email
 * @property {{ en: string, ar: string }} fullName
 * @property {string} role
 * @property {string[]} permissions
 * @property {string} portal
 * @property {string} [status]
 */

const AuthContext = createContext(null);

function isSuspendedUser(user) {
  return user?.status === 'SUSPENDED';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const checkingSession = useRef(false);

  const handleUnauthorized = useCallback((reason) => {
    setUser(null);
    navigate('/login', {
      replace: true,
      state: reason ? { reason } : undefined,
    });
  }, [navigate]);

  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  const endSession = useCallback(async (reason) => {
    try {
      await authApi.logout();
    } catch {
      // ignore logout errors
    } finally {
      clearTokens();
      setUser(null);
      navigate('/login', {
        replace: true,
        state: reason ? { reason } : undefined,
      });
    }
  }, [navigate]);

  const applyUser = useCallback((nextUser) => {
    if (isSuspendedUser(nextUser)) {
      endSession('suspended');
      return false;
    }
    setUser(nextUser);
    return true;
  }, [endSession]);

  const applyUserRef = useRef(applyUser);
  applyUserRef.current = applyUser;

  const validateSession = useCallback(async () => {
    if (checkingSession.current) return;
    if (!getAccessToken() && !getRefreshToken()) return;

    checkingSession.current = true;
    try {
      const me = await authApi.getMe();
      applyUserRef.current(me);
    } catch {
      // Force-logout errors are handled by the API client interceptor.
    } finally {
      checkingSession.current = false;
    }
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const storedRefresh = getRefreshToken();
      if (!storedRefresh) {
        setLoading(false);
        return;
      }
      try {
        const tokens = await authApi.refresh(storedRefresh);
        setTokens(tokens.accessToken, tokens.refreshToken || storedRefresh);
        const me = await authApi.getMe();
        if (!applyUserRef.current(me)) return;
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    const intervalId = window.setInterval(validateSession, SESSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [user, validateSession]);

  const login = useCallback(async (email, password) => {
    const tokens = await authApi.login({ email, password });
    setTokens(tokens.accessToken, tokens.refreshToken);
    const nextUser = tokens.user || (await authApi.getMe());
    if (isSuspendedUser(nextUser)) {
      clearTokens();
      const err = new Error('User suspended');
      err.apiError = { code: 'USER_SUSPENDED' };
      throw err;
    }
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    await endSession();
  }, [endSession]);

  const value = useMemo(
    () => ({ user, loading, isAuthenticated: !!user, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
